import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { testimonialService } from '../../services/testimonialService.js';
import { Plus, Filter, ArrowLeft, Pencil, Package, MoreVertical, HandCoins, XCircle, CreditCard, MessageSquareQuote } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const ViewTestimonial = () => {
  const { t } = useTranslation();
  const { testimonialId } = useParams();
  const [testimonial, setTestimonial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTestimonial();
  }, [testimonialId]);

  const fetchTestimonial = async () => {
    try {
      const response = await testimonialService.getTestimonial(testimonialId);
      setTestimonial(response.data.data);
    } catch (err) {
      Alert.error('Failed to load testimonial details');
      console.error(err);
      navigate('/testimonials');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating) => {
    return '⭐'.repeat(rating);
  };

  const getImageSrc = () => {
    if (testimonial && testimonial.image_base64) {
      return `data:image/jpeg;base64,${testimonial.image_base64}`;
    }
    return null;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-3 sm:mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading testimonial details...</p>
            </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!testimonial) {
    return null;
  }

  const imageSrc = getImageSrc();

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <MessageSquareQuote className="w-6 h-6 text-buttonBg" /> Testimonial Details
                  </h1>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  {/* Header Section */}
                  <div className="bg-primary p-4 sm:p-6 md:p-8 md:py-4">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 overflow-hidden border-4 border-white">
                        {imageSrc && !imageError ? (
                          <img 
                            src={imageSrc} 
                            alt={testimonial.full_name}
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                          />
                        ) : (
                          <span className="text-4xl sm:text-5xl">👤</span>
                        )}
                      </div>
                      <div className="flex-1 text-center sm:text-left">
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-tertiary mb-0">{testimonial.full_name}</h2>
                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-4">
                          <p className="text-tertiary text-base sm:text-lg">{testimonial.location}</p>
                          <span className={`px-2.5 sm:px-3 py-1 rounded-xl text-xs sm:text-sm font-semibold ${
                            testimonial.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {testimonial.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    {/* Customer Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Customer Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Testimonial ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{testimonial.id}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Full Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{testimonial.full_name}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Location</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{testimonial.location}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Status</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            {testimonial.is_active ? 'Active' : 'Inactive'}
                          </p>
                        </div>
                        {imageSrc && !imageError && (
                          <div className="sm:col-span-2">
                            <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Customer Image</label>
                            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-lg overflow-hidden border-2 border-gray-200">
                              <img 
                                src={imageSrc} 
                                alt={testimonial.full_name}
                                className="w-full h-full object-cover"
                                onError={() => setImageError(true)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Testimonial Content */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Testimonial Content
                      </h3>
                      <div className="space-y-4 sm:space-y-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Rating</label>
                          <div className="flex items-center gap-2">
                            <span className="text-xl sm:text-2xl text-yellow-500">{renderStars(testimonial.rating)}</span>
                            <span className="text-sm sm:text-base text-gray-700 font-medium">({testimonial.rating}/5)</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Testimonial Text</label>
                          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                            <p className="text-sm sm:text-base text-gray-900 leading-relaxed italic">
                              "{testimonial.testimonial_text}"
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">{t('common.createdBy')}</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{testimonial.created_by || 'System'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all break-words">{formatDate(testimonial.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Last Updated By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{testimonial.updated_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Last Updated At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all break-words">{formatDate(testimonial.updated_at)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Additional Information
                      </h3>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <span className="text-xl sm:text-2xl flex-shrink-0">ℹ️</span>
                          <div>
                            <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">About Testimonials</h4>
                            <p className="text-xs sm:text-sm text-gray-700">
                              Customer testimonials help build trust and credibility. Active testimonials are displayed
                              on the public-facing website to showcase customer satisfaction and experiences.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="bg-gray-50 px-4 sm:px-6 md:px-8 py-4 sm:py-6 flex flex-col sm:flex-row gap-3 sm:gap-4 border-t">
                    <Button variant="secondary" onClick={() => navigate(`/testimonials/edit/${testimonialId}`)} icon={Pencil}>
                      Edit Testimonial
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/testimonials')} icon={ArrowLeft}>
                      Back to Testimonials
                    </Button>
                  </div>
                </div>
              </div>
    </DashboardLayout>
  );
};

export default ViewTestimonial;